import * as crosstab from "crosstab";
import { AuthConfigImpl } from "../common/auth.config";
import { IApiClient } from "../common/api.client";
import { UserStore } from "./user.store";
import { AppContextStore } from "./app.context.store";
import {
	UserClient,
	ReportClient,
	LookupClient,
	ContextClient,
	QuestionnaireClient,
	TrainingContentRecommendationsClient,
	QuestionnaireAnswerClient,
	LayoutClient,
	DebugPanelClient,
} from "../common/webapicall";
import { IConfigurationProvider, AppPublicConfiguration } from "../common/public.configuration";
import { DebugPanelStore } from "./debug.panel.store";
import { observable } from "mobx";
import { QuestionnaireStore } from "./questionnaire.store";
import { TrainingContentStore } from "./training.content.store";
import { ReportPageStore } from "./report.page.store";
import { LayoutStore } from "./layout.store";
import { hotjar } from 'react-hotjar';
import ReactGA from 'react-ga';
import TagManager from 'react-gtm-module'

export interface IAppStore extends IConfigurationProvider {
	init();
}

const RESIZE_OBSERVER_ERROR_MESSAGE = "ResizeObserver loop limit exceeded";

abstract class AppStoreBase implements IAppStore {
	abstract userStore: UserStore;
	abstract configuration: AppPublicConfiguration;
	abstract outDatedClient: boolean = false;
	abstract errorVisible: boolean = false;
	abstract errorMessage: string = null;

	abstract questionnaireStore: QuestionnaireStore;
	abstract reportPageStore: ReportPageStore;
	abstract trainingContentStore: TrainingContentStore;
	abstract layoutStore: LayoutStore;

	abstract init();

	protected subscribeEventHandlers() {
		window.addEventListener("error", async (ev) => {
			if (ev && ev.message === RESIZE_OBSERVER_ERROR_MESSAGE) {
				return;
			}
			if (typeof ev?.error?.stack === "string" && ev.error.stack.indexOf("crosstab.js")) {
				//console.log("supress crosstab.js errors.");
				return true;
			}

			await this.reportError(ev?.error?.stack);
		});
		window.onunhandledrejection = async (ev) => await this.reportUnhandledRejection(ev.reason);
	}

	protected setupCrosstab(tokenToCheck: string) {
		if (crosstab.supported) {
			let ctEventName: string = "crossTabOpenNewTab";
			crosstab.on(ctEventName, (message) => {
				if (crosstab.id === message.origin) {
					//console.log("Crosstab receiving event on the same tab, skip it", ct.id, JSON.stringify(message));
				} else {
					//console.log("Crosstab receiving event on a different tab, process it", ct.id, JSON.stringify(message));
					if (tokenToCheck !== message.data) {
						window.location.assign("#/forced-logout");
						return;
					}
				}
			});
			//console.log("Crosstab broadcast event from source tab", ct.id);
			crosstab.broadcast(ctEventName, tokenToCheck);
		}
	}

	private async reportUnhandledRejection(reason: any) {
		if (reason.isWebApiErrorResponse) {
			if (reason.statusCode === 406) {
				this.outDatedClient = true;
			} else if (reason.statusCode === 401) {
				if (this.userStore.isLoggedIn) {
					window.location.href = "#/session-expired";
					await this.userStore.logout();
				} else {
					window.location.href = this.userStore.getLoginUrl();
				}
			} else {
				await this.reportError(`Unhandled web api error:
					URL: ${reason.url} (${reason.statusCode})

					Body: ${reason.body}

					Stack: ${reason.stack}`);
			}
		} else {
			await this.reportError(`Unhandled rejection: ${reason}`);
		}
	}

	private async reportError(reason: any) {
		try {
			this.errorVisible = true;
			if (this.configuration.showErrorDetails) {
				this.errorMessage = JSON.stringify(reason);
			}

			await this.sendClientException(reason);
		} catch (e) {
			// Just log it console as reporting it would be recursive.
			console.log("Error occurred during reporting JS error");
			console.log(e);
		}
	}

	protected abstract sendClientException(reason: any): Promise<void>;
}

export class AppStore extends AppStoreBase implements IConfigurationProvider {
	private authConfig: AuthConfigImpl;

	apiClient: IApiClient;
	userStore: UserStore;
	contextStore: AppContextStore;
	debugPanelStore: DebugPanelStore;
	questionnaireStore: QuestionnaireStore;
	reportPageStore: ReportPageStore;
	trainingContentStore: TrainingContentStore;
	layoutStore: LayoutStore;

	configuration: AppPublicConfiguration;

	@observable outDatedClient: boolean = false;
	@observable errorVisible: boolean = false;
	@observable errorMessage: string = null;

	constructor() {
		super();
		this.authConfig = new AuthConfigImpl(this);
		this.apiClient = {
			setAuthToken: (newToken: string, refreshToken: string) => {
				this.authConfig.token = newToken;
				this.authConfig.refreshToken = refreshToken;
			},
			contextClient: new ContextClient(this.authConfig, null, this.authConfig),
			lookupClient: new LookupClient(this.authConfig, null, this.authConfig),
			reportClient: new ReportClient(this.authConfig, null, this.authConfig),
			userClient: new UserClient(this.authConfig, null, this.authConfig),
			questionnaireClient: new QuestionnaireClient(this.authConfig, null, this.authConfig),
			trainingContentClient: new TrainingContentRecommendationsClient(this.authConfig, null, this.authConfig),
			questionnaireAnswerClient: new QuestionnaireAnswerClient(this.authConfig, null, this.authConfig),
			layoutClient: new LayoutClient(this.authConfig, null, this.authConfig),
			debugPanelClient: new DebugPanelClient(this.authConfig, null, this.authConfig),
		};
		this.userStore = new UserStore(this.apiClient, this);
		this.contextStore = new AppContextStore(this.apiClient);
		this.debugPanelStore = new DebugPanelStore(this.userStore, this.apiClient);
		this.questionnaireStore = new QuestionnaireStore(this.apiClient);
		this.reportPageStore = new ReportPageStore(this.apiClient, this);
		this.trainingContentStore = new TrainingContentStore(this.apiClient);
		this.layoutStore = new LayoutStore(this.apiClient);
	}

	async init() {
		await this.loadApiVersion();

		await this.subscribeEventHandlers();
		this.userStore.initTokens();
		await this.layoutStore.getSiteLayout();

		await this.loadPublicConfiguration();
		this.addUsageTrackingScripts();
		await this.userStore.init();

		if (this.userStore.userId) {
			await this.contextStore.loadContext(this.userStore.userId);
		}

		await this.setupCrosstab(this.userStore.getAuthToken());
	}

	async loadPublicConfiguration() {
		let config = await this.apiClient.lookupClient.getPublicConfiguration();
		this.configuration = {
			...config,
			loginUrl: "/#/login",
		};
	}

	protected async sendClientException(reason: any): Promise<void> {
		await this.apiClient.reportClient.sendClientException({
			message: reason,
			url: window.location.href,
		});
	}

	private async loadApiVersion() {
		(window as any).apiVersion = await this.apiClient.lookupClient.getApiVersion();
	}

	private addUsageTrackingScripts() {
		if (this.configuration.hotjarId) {			
			hotjar.initialize(this.configuration.hotjarId, 6);
		};

		if (this.configuration.googleAnalyticsId) {				
			ReactGA.initialize(this.configuration.googleAnalyticsId);			
			ReactGA.pageview(window.location.pathname + window.location.search);
		}

		if (this.configuration.googleTagManagerId) {					
			const tagManagerArgs = {
				gtmId: this.configuration.googleTagManagerId
			}
			TagManager.initialize(tagManagerArgs)
		}
	}
}
